import React, { useState, useMemo } from "react";
import { Upload, X, ChevronDown } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { SCENE_CATEGORIES, groupScenesByCategory } from "../lib/sceneCategories";

/**
 * SceneCategoryPicker — colonne latérale droite.
 * Présentation compacte par défaut (V3, demandée par l'utilisateur) :
 *  - Façade   : 3 vignettes d'aperçu (immeuble / maison / bâtiment tôlé)
 *  - Angle    : 1 vignette d'aperçu
 *  - Intérieur: 1 vignette d'aperçu
 *  - Chaque catégorie a un bouton « Voir plus (N) » qui déroule la liste
 *    complète. Le titre de la section reste sticky en haut pendant le
 *    scroll interne.
 */
const PREVIEW_COUNTS = { facade: 3, angle: 1, interior: 1 };

export default function SceneCategoryPicker({
  scenes,
  selectedId,
  onSelect,
  customScene,
  onUploadClick,
  onClearCustom,
  thumbnailTestIdPrefix = "scene",
}) {
  const { lang } = useI18n();
  const groups = useMemo(() => groupScenesByCategory(scenes), [scenes]);

  // État "déroulé" par catégorie. Par défaut, si la scène sélectionnée n'est
  // pas dans le preview de sa catégorie, on déroule automatiquement cette
  // catégorie pour que l'utilisateur voie son choix.
  const initialExpanded = useMemo(() => {
    const out = { facade: false, angle: false, interior: false };
    if (!selectedId) return out;
    for (const cat of SCENE_CATEGORIES) {
      const items = groups[cat.key] || [];
      const preview = items.slice(0, PREVIEW_COUNTS[cat.key] || 1);
      const isInPreview = preview.some((s) => s.id === selectedId);
      const isInGroup = items.some((s) => s.id === selectedId);
      if (isInGroup && !isInPreview) out[cat.key] = true;
    }
    return out;
  }, [groups, selectedId]);

  const [expanded, setExpanded] = useState(initialExpanded);

  const toggle = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  return (
    <div
      className="h-full max-h-[78vh] lg:max-h-none lg:absolute lg:inset-0 flex flex-col gap-3 overflow-y-auto pr-1 lg:pr-2"
      data-testid="scene-category-picker"
    >
      {/* ----- Upload custom photo ----- */}
      <button
        type="button"
        onClick={onUploadClick}
        data-testid="upload-scene"
        className={`relative w-full aspect-square border flex flex-col items-center justify-center gap-1.5 shrink-0 ${
          customScene ? "border-[#39FF14] bg-[#39FF14]/10" : "border-dashed border-white/30 hover:border-[#39FF14]/60"
        }`}
      >
        <Upload size={18} className={customScene ? "text-[#39FF14]" : "text-zinc-400"} />
        <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-300 px-2 text-center leading-tight">
          {customScene ? (lang === "fr" ? "Ma photo ✓" : "My photo ✓") : (lang === "fr" ? "Importer photo" : "Upload photo")}
        </span>
        {customScene && (
          <span
            onClick={(e) => { e.stopPropagation(); onClearCustom?.(); }}
            className="absolute top-1 right-1 text-zinc-300 hover:text-red-400"
          >
            <X size={12} />
          </span>
        )}
      </button>

      {/* ----- 3 sections (Façade / Angle / Intérieur) ----- */}
      {SCENE_CATEGORIES.map((cat) => {
        const items = groups[cat.key] || [];
        if (items.length === 0) return null;
        const labelStr = cat.label[lang] || cat.label.fr;
        const previewCount = PREVIEW_COUNTS[cat.key] || 1;
        const isExpanded = expanded[cat.key];
        const visibleItems = isExpanded ? items : items.slice(0, previewCount);
        const remaining = items.length - previewCount;
        return (
          <section
            key={cat.key}
            data-testid={`scene-category-${cat.key}`}
            className="shrink-0"
          >
            {/* Titre sticky */}
            <div
              className="sticky top-0 z-10 -mx-0.5 px-1 py-1.5 mb-1.5 bg-black/95 backdrop-blur-sm border-b border-[#39FF14]/30"
              data-testid={`scene-category-title-${cat.key}`}
            >
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#39FF14]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#39FF14]">
                  {labelStr}
                </span>
                <span className="font-mono text-[9px] text-zinc-500 ml-auto">{items.length}</span>
              </div>
            </div>
            {/* Thumbnails (preview ou liste complète) */}
            <div className="grid grid-cols-1 gap-1.5">
              {visibleItems.map((s) => {
                const selected = !customScene && selectedId === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => onSelect?.(s.id)}
                    data-testid={`${thumbnailTestIdPrefix}-${s.id}`}
                    className={`relative w-full aspect-square overflow-hidden border transition-all ${
                      selected
                        ? "border-[#39FF14] neon-glow-sm"
                        : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <img
                      src={s.image}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ objectPosition: "center bottom" }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent" />
                    <div className="absolute bottom-1 left-1.5 right-1.5 font-mono text-[8px] uppercase tracking-wider text-white truncate">
                      {s.label.fr}
                    </div>
                  </button>
                );
              })}
            </div>
            {/* Bouton « Voir plus / Voir moins » */}
            {remaining > 0 && (
              <button
                type="button"
                onClick={() => toggle(cat.key)}
                data-testid={`scene-category-expand-${cat.key}`}
                className="mt-1.5 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 font-mono text-[9px] uppercase tracking-[0.22em] border border-white/15 text-zinc-300 hover:border-[#39FF14]/60 hover:text-[#39FF14] transition-all"
              >
                <ChevronDown
                  size={11}
                  className={`transition-transform ${isExpanded ? "rotate-180" : ""}`}
                />
                {isExpanded
                  ? (lang === "fr" ? "Voir moins" : "Show less")
                  : (lang === "fr" ? `Voir plus (${remaining})` : `More (${remaining})`)}
              </button>
            )}
          </section>
        );
      })}
    </div>
  );
}
